import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { TopPage } from "@/components/elements/TopPage";
import { PartnerStatusTag } from "@/features/rso/components/PartnerStatusTag";
import { Stack, styled, Typography } from "@mui/material";
import { FC, useEffect } from "react";
import { ECompanyContractStatus } from "@/types/enum";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { useParams, useNavigate } from "react-router-dom";
import { AppRoutes } from "@/routes/config";
import { formatCompanyId, isNumber } from "@/utils/number";
import { formatDateTime } from "@/utils/datetime";
import { CompanyBasicInfoDetail } from "@/features/company/components/detail/CompanyBasicInfoDetail";
import { CompanyDescriptionDetail } from "@/features/company/components/detail/CompanyDescriptionDetail";
import { CompanyUserListDetail } from "@/features/company/components/detail/CompanyUserListDetail";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Button } from "@/components/elements/Button";
import { getDynamicRoute } from "@/utils/url";
import { deleteCompanyApi, useCompany } from "@/api/services/admin/company";
import { useDialog } from "@/hooks/useDialog";
import { useToast } from "@/hooks/useToast";
import { showError } from "@/utils/error";
import { useQueryClient } from "@tanstack/react-query";
import { IconTrash } from "@/assets/icons/IconTrash";
import { Tag } from "@/components/elements/Tag";
import { black } from "@/theme/colors";

const DELETABLE_CONTRACT_STATUSES: string[] = [ECompanyContractStatus.TRIAL_CANCELLED, ECompanyContractStatus.CANCELED_CONTRACT];

export const CompanyDetail: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirmDelete = useDialog();
  // call api
  const { data: company, isLoading } = useCompany();

  const isDeleted = !!company?.data?.deletedAt;
  const canDelete = company?.data ? DELETABLE_CONTRACT_STATUSES.includes(company.data.contractStatus) : false;

  const handleDelete = async () => {
    try {
      if (!id) return;
      const agreeToDelete = await confirmDelete.show();
      if (!agreeToDelete) return;
      const rs = await deleteCompanyApi(id);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      navigate(AppRoutes.companyDuringContractList);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY.FETCH_LIST] });
    } catch (error) {
      showError(error);
    }
  };

  useEffect(() => {
    if (!id || !isNumber(id)) {
      navigate(AppRoutes.companyDuringContractList, { replace: true });
    }
  }, [id, navigate]);

  useEffect(() => {
    if (id && isNumber(id) && !isLoading && !company) {
      navigate(AppRoutes.companyDuringContractList, { replace: true });
    }
  }, [id, isLoading, company, navigate]);

  if (isLoading || !company) return <LoadingPage />;

  return (
    <CompanyDetailWrapper gap={3}>
      <TopPage
        title={
          <Typography variant="head32Bold" display="flex" alignItems="flex-end" gap={2}>
            {company?.data.companyName}
            <PartnerStatusTag status={company?.data.contractStatus} variant="normal" />
          </Typography>
        }
        description={
          <Typography variant="body14">
            組織ID：{formatCompanyId(company?.data.id) ?? "----"}
            <span style={{ marginLeft: "16px" }}>最終ログイン日時：{formatDateTime(company?.data.lastLoginTime)}</span>
          </Typography>
        }
        rightContent={
          isDeleted ? (
            <Tag enablePoint variant="bold" title="削除済み" color={black[95]} pointColor={black[70]} />
          ) : (
            canDelete && (
              <Button
                size="sm"
                variant={"danger"}
                startIcon={<IconTrash />}
                sx={{
                  width: "88px",
                }}
                onClick={handleDelete}
              >
                削除
              </Button>
            )
          )
        }
        enablePageBack={false}
      />
      <MenuNavigate
        contents={[
          { title: "基本情報", content: <CompanyBasicInfoDetail company={company?.data} /> },
          { title: "その他情報", content: <CompanyDescriptionDetail company={company?.data} /> },
          { title: "顧客ユーザーの一覧", content: <CompanyUserListDetail isDeleted={isDeleted} /> },
        ]}
        contentWidth="900px"
        contentAffix={
          <FooterContainer>
            <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
              <Button variant="secondary" size="sm" onClick={() => navigate(AppRoutes.companyDuringContractList)}>
                戻る
              </Button>
              {!isDeleted && (
                <Button type="button" variant="primary" size="sm" onClick={() => navigate(getDynamicRoute(AppRoutes.companyEdit, { id }))}>
                  編集
                </Button>
              )}
            </Stack>
          </FooterContainer>
        }
      />
      <ConfirmDeleteModal
        open={confirmDelete.open}
        onClose={confirmDelete.cancel}
        onOk={confirmDelete.confirm}
        message="この組織を削除してもよろしいですか？"
      />
    </CompanyDetailWrapper>
  );
};

const CompanyDetailWrapper = styled(Stack)`
  margin: 0 auto;
`;
